<template>
  <div class="announcement-page">
    <!-- 页面头部 -->
    <div class="page-header">
      <div class="header-left">
        <h2>系统公告</h2>
        <p class="subtitle">管理客户端启动时显示的公告内容</p>
      </div>
      <div class="header-status">
        <span class="status-dot" :class="{ active: localEnabled }"></span>
        <span class="status-text">{{ localEnabled ? '公告已开启' : '公告已关闭' }}</span>
      </div>
    </div>

    <!-- 主内容区 -->
    <div class="content-grid">
      <!-- 左侧：编辑区 -->
      <div class="editor-card">
        <div class="card-header">
          <h3>公告内容</h3>
          <label class="switch">
            <input 
              type="checkbox" 
              v-model="localEnabled" 
              :disabled="loading"
            />
            <span class="slider"></span>
          </label>
        </div>

        <div class="editor-body">
          <div class="toolbar">
            <button 
              v-for="tag in htmlTags" 
              :key="tag.name"
              class="tag-btn"
              @click="insertTag(tag.value)"
              :title="tag.title"
            >
              {{ tag.label }}
            </button>
          </div>

          <textarea
            ref="editorRef"
            v-model="localContent"
            class="editor-textarea"
            rows="12"
            placeholder="请输入公告内容，支持 HTML 标签..."
            :disabled="loading"
          ></textarea>

          <div class="editor-footer">
            <span class="char-count">{{ localContent.length }} 字符</span>
            <span class="last-save" v-if="lastSaveTime">
              上次保存: {{ formatTime(lastSaveTime) }}
            </span>
          </div>
        </div>

        <div class="card-actions">
          <button 
            class="btn btn-secondary" 
            @click="fetchData"
            :disabled="loading"
          >
            <svg viewBox="0 0 20 20" fill="currentColor" style="width:16px;height:16px">
              <path fill-rule="evenodd" d="M4 2a1 1 0 011 1v2.101a7.002 7.002 0 0111.601 2.566 1 1 0 11-1.885.666A5.002 5.002 0 005.999 7H9a1 1 0 010 2H4a1 1 0 01-1-1V3a1 1 0 011-1zm.008 9.057a1 1 0 011.276.61A5.002 5.002 0 0014.001 13H11a1 1 0 110-2h5a1 1 0 011 1v5a1 1 0 11-2 0v-2.101a7.002 7.002 0 01-11.601-2.566 1 1 0 01.61-1.276z" clip-rule="evenodd"/>
            </svg>
            刷新
          </button>
          <button 
            class="btn btn-primary" 
            @click="saveData"
            :disabled="saving || loading || !localContent.trim()"
          >
            <svg v-if="!saving" viewBox="0 0 20 20" fill="currentColor" style="width:16px;height:16px">
              <path fill-rule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clip-rule="evenodd"/>
            </svg>
            <svg v-else class="spin" viewBox="0 0 20 20" fill="currentColor" style="width:16px;height:16px">
              <path fill-rule="evenodd" d="M4 2a1 1 0 011 1v2.101a7.002 7.002 0 0111.601 2.566 1 1 0 11-1.885.666A5.002 5.002 0 005.999 7H9a1 1 0 010 2H4a1 1 0 01-1-1V3a1 1 0 011-1z" clip-rule="evenodd"/>
            </svg>
            {{ saving ? '保存中...' : '保存公告' }}
          </button>
        </div>
      </div>

      <!-- 右侧：预览区 -->
      <div class="preview-card">
        <div class="card-header">
          <h3>客户端预览</h3>
          <span class="preview-badge">实时</span>
        </div>

        <div class="preview-body">
          <!-- 模拟客户端弹窗 -->
          <div class="mock-dialog">
            <div class="mock-header">
              <span class="mock-icon">📢</span>
              <span class="mock-title">系统公告</span>
              <button class="mock-close">×</button>
            </div>
            <div class="mock-content">
              <div v-if="localContent" class="announcement-render" v-html="localContent"></div>
              <div v-else class="empty-preview">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
                  <path d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/>
                </svg>
                <p>暂无公告内容</p>
                <span>输入内容后将在此处预览</span>
              </div>
            </div>
            <div class="mock-footer">
              <button class="mock-btn">我知道了</button>
            </div>
          </div>
        </div>

        <div class="preview-tips">
          <h4>💡 使用提示</h4>
          <ul>
            <li>支持 HTML 标签：&lt;b&gt;粗体&lt;/b&gt;、&lt;i&gt;斜体&lt;/i&gt;</li>
            <li>支持换行：使用 &lt;br&gt; 或按回车</li>
            <li>支持链接：&lt;a href="..."&gt;链接文字&lt;/a&gt;</li>
            <li>建议公告长度不超过 500 字符</li>
          </ul>
        </div>
      </div>
    </div>

    <!-- 错误提示 -->
    <div v-if="error" class="error-toast">
      <svg viewBox="0 0 20 20" fill="currentColor" style="width:18px;height:18px">
        <path fill-rule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clip-rule="evenodd"/>
      </svg>
      <span>{{ error }}</span>
      <button @click="error = ''">×</button>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, watch } from 'vue'

// API 基础地址
const API_BASE = import.meta.env.VITE_API_BASE || ''

// 状态
const localContent = ref('')
const localEnabled = ref(false)
const loading = ref(false)
const saving = ref(false)
const error = ref('')
const lastSaveTime = ref(null)
const editorRef = ref(null)

// HTML 快捷标签
const htmlTags = [
  { name: 'bold', label: 'B', value: '<b>文本</b>', title: '粗体' },
  { name: 'italic', label: 'I', value: '<i>文本</i>', title: '斜体' },
  { name: 'br', label: '↵', value: '<br>', title: '换行' },
  { name: 'link', label: '🔗', value: '<a href="">链接</a>', title: '链接' },
  { name: 'color', label: '🎨', value: '<span style="color:#ff6b00">文本</span>', title: '颜色' },
]

// 获取 Token
function getToken() {
  return localStorage.getItem('admin_token')
}

// 获取公告数据
async function fetchData() {
  loading.value = true
  error.value = ''
  
  try {
    const res = await fetch(`${API_BASE}/api/announcement/admin/list`, {
      headers: {
        'Authorization': `Bearer ${getToken()}`
      }
    })
    
    if (!res.ok) {
      if (res.status === 401) {
        throw new Error('登录已过期，请重新登录')
      }
      throw new Error(`请求失败: ${res.status}`)
    }
    
    const data = await res.json()
    
    if (data.success && data.announcements && data.announcements.length > 0) {
      const ann = data.announcements[0]
      localContent.value = ann.content || ''
      localEnabled.value = !!ann.is_enabled
    } else {
      localContent.value = ''
      localEnabled.value = false
    }
  } catch (e) {
    console.error('[Announcement] Fetch error:', e)
    error.value = e.message || '获取公告失败'
  } finally {
    loading.value = false
  }
}

// 保存公告
async function saveData() {
  saving.value = true
  error.value = ''
  
  try {
    const res = await fetch(`${API_BASE}/api/announcement/admin`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${getToken()}`
      },
      body: JSON.stringify({
        content: localContent.value,
        is_enabled: localEnabled.value
      })
    })
    
    if (!res.ok) {
      if (res.status === 401) {
        throw new Error('登录已过期，请重新登录')
      }
      throw new Error(`请求失败: ${res.status}`)
    }
    
    const data = await res.json()
    
    if (data.success) {
      lastSaveTime.value = new Date()
      // 触发全局提示事件
      window.dispatchEvent(new CustomEvent('admin-toast', {
        detail: { 
          message: localEnabled.value ? '公告已保存并开启' : '公告已保存（未开启）', 
          type: 'success' 
        }
      }))
    } else {
      throw new Error(data.message || '保存失败')
    }
  } catch (e) {
    console.error('[Announcement] Save error:', e)
    error.value = e.message || '保存公告失败'
  } finally {
    saving.value = false
  }
}

// 插入标签
function insertTag(tagValue) {
  const textarea = editorRef.value
  if (!textarea) return
  
  const start = textarea.selectionStart
  const end = textarea.selectionEnd
  const text = localContent.value
  
  localContent.value = text.substring(0, start) + tagValue + text.substring(end)
  
  // 恢复焦点并选中文本
  setTimeout(() => {
    textarea.focus()
    const newCursor = start + tagValue.indexOf('文本')
    if (tagValue.includes('文本')) {
      textarea.setSelectionRange(newCursor, newCursor + 2)
    } else {
      textarea.setSelectionRange(start + tagValue.length, start + tagValue.length)
    }
  }, 0)
}

// 格式化时间
function formatTime(date) {
  if (!date) return '-'
  const d = new Date(date)
  return d.toLocaleString('zh-CN', {
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit'
  })
}

// 初始化
onMounted(() => {
  fetchData()
})
</script>

<style scoped>
.announcement-page {
  padding: 24px;
  max-width: 1400px;
  margin: 0 auto;
}

/* 页面头部 */
.page-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
  padding-bottom: 20px;
  border-bottom: 1px solid var(--border, #e5e7eb);
}

.header-left h2 {
  font-size: 24px;
  font-weight: 600;
  color: var(--text, #111827);
  margin: 0 0 4px;
}

.subtitle {
  font-size: 14px;
  color: var(--text-2, #6b7280);
  margin: 0;
}

.header-status {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 16px;
  background: var(--bg, #f3f4f6);
  border-radius: 20px;
}

.status-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: #9ca3af;
  transition: all 0.3s;
}

.status-dot.active {
  background: #10b981;
  box-shadow: 0 0 0 3px rgba(16, 185, 129, 0.2);
}

.status-text {
  font-size: 14px;
  font-weight: 500;
  color: var(--text-2, #6b7280);
}

/* 内容网格 */
.content-grid {
  display: grid;
  grid-template-columns: 1fr 420px;
  gap: 24px;
}

@media (max-width: 1100px) {
  .content-grid {
    grid-template-columns: 1fr;
  }
}

/* 卡片样式 */
.editor-card,
.preview-card {
  background: var(--surface, #fff);
  border: 1px solid var(--border, #e5e7eb);
  border-radius: 16px;
  overflow: hidden;
}

.card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px 24px;
  border-bottom: 1px solid var(--border, #e5e7eb);
  background: linear-gradient(to right, var(--bg, #f9fafb), transparent);
}

.card-header h3 {
  font-size: 16px;
  font-weight: 600;
  color: var(--text, #111827);
  margin: 0;
}

/* 开关 */
.switch {
  position: relative;
  display: inline-flex;
  align-items: center;
  cursor: pointer;
}

.switch input {
  opacity: 0;
  width: 0;
  height: 0;
}

.slider {
  position: relative;
  width: 48px;
  height: 26px;
  background: #d1d5db;
  border-radius: 26px;
  transition: all 0.3s;
}

.slider::before {
  content: '';
  position: absolute;
  height: 20px;
  width: 20px;
  left: 3px;
  bottom: 3px;
  background: white;
  border-radius: 50%;
  transition: all 0.3s;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
}

.switch input:checked + .slider {
  background: #4f46e5;
}

.switch input:checked + .slider::before {
  transform: translateX(22px);
}

/* 编辑器 */
.editor-body {
  padding: 20px 24px;
}

.toolbar {
  display: flex;
  gap: 8px;
  margin-bottom: 12px;
  padding-bottom: 12px;
  border-bottom: 1px solid var(--border-light, #f3f4f6);
}

.tag-btn {
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 1px solid var(--border, #e5e7eb);
  border-radius: 6px;
  background: var(--surface, #fff);
  color: var(--text-2, #6b7280);
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
}

.tag-btn:hover {
  border-color: #4f46e5;
  color: #4f46e5;
  background: #eef2ff;
}

.editor-textarea {
  width: 100%;
  padding: 16px;
  border: 1px solid var(--border, #e5e7eb);
  border-radius: 10px;
  background: var(--bg, #f9fafb);
  color: var(--text, #111827);
  font-size: 14px;
  line-height: 1.7;
  font-family: 'SF Mono', Monaco, monospace;
  resize: vertical;
  min-height: 200px;
  box-sizing: border-box;
  transition: all 0.2s;
}

.editor-textarea:focus {
  outline: none;
  border-color: #4f46e5;
  background: var(--surface, #fff);
  box-shadow: 0 0 0 3px rgba(79, 70, 229, 0.1);
}

.editor-textarea:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.editor-footer {
  display: flex;
  justify-content: space-between;
  margin-top: 12px;
  font-size: 12px;
  color: var(--text-3, #9ca3af);
}

/* 按钮 */
.card-actions {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  padding: 16px 24px;
  border-top: 1px solid var(--border, #e5e7eb);
  background: var(--bg, #f9fafb);
}

.btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 10px 20px;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  border: 1px solid transparent;
}

.btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.btn-primary {
  background: #4f46e5;
  color: white;
}

.btn-primary:hover:not(:disabled) {
  background: #4338ca;
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(79, 70, 229, 0.3);
}

.btn-secondary {
  background: white;
  color: var(--text-2, #6b7280);
  border-color: var(--border, #e5e7eb);
}

.btn-secondary:hover:not(:disabled) {
  border-color: #4f46e5;
  color: #4f46e5;
}

.spin {
  animation: spin 1s linear infinite;
}

@keyframes spin {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}

/* 预览区 */
.preview-badge {
  padding: 4px 10px;
  background: #dbeafe;
  color: #1e40af;
  font-size: 12px;
  font-weight: 600;
  border-radius: 12px;
}

.preview-body {
  padding: 24px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  min-height: 320px;
  display: flex;
  align-items: center;
  justify-content: center;
}

/* 模拟弹窗 */
.mock-dialog {
  width: 100%;
  max-width: 320px;
  background: white;
  border-radius: 12px;
  box-shadow: 0 20px 60px rgba(0,0,0,0.3);
  overflow: hidden;
}

.mock-header {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 16px 20px;
  background: linear-gradient(to right, #4f46e5, #7c3aed);
  color: white;
}

.mock-icon {
  font-size: 18px;
}

.mock-title {
  flex: 1;
  font-size: 15px;
  font-weight: 600;
}

.mock-close {
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(255,255,255,0.2);
  border: none;
  border-radius: 50%;
  color: white;
  font-size: 18px;
  cursor: pointer;
}

.mock-content {
  padding: 20px;
  min-height: 100px;
  max-height: 200px;
  overflow-y: auto;
}

.announcement-render {
  font-size: 14px;
  line-height: 1.7;
  color: #374151;
}

.announcement-render :deep(b),
.announcement-render :deep(strong) {
  color: #4f46e5;
}

.announcement-render :deep(a) {
  color: #4f46e5;
  text-decoration: underline;
}

.empty-preview {
  text-align: center;
  color: #9ca3af;
}

.empty-preview svg {
  width: 48px;
  height: 48px;
  margin: 0 auto 12px;
  opacity: 0.5;
}

.empty-preview p {
  font-size: 14px;
  margin: 0 0 4px;
}

.empty-preview span {
  font-size: 12px;
}

.mock-footer {
  padding: 12px 20px 20px;
  text-align: center;
}

.mock-btn {
  padding: 10px 32px;
  background: #4f46e5;
  color: white;
  border: none;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
}

/* 提示 */
.preview-tips {
  padding: 20px 24px;
  border-top: 1px solid var(--border, #e5e7eb);
}

.preview-tips h4 {
  font-size: 13px;
  font-weight: 600;
  color: var(--text, #111827);
  margin: 0 0 12px;
}

.preview-tips ul {
  margin: 0;
  padding-left: 16px;
  font-size: 12px;
  color: var(--text-2, #6b7280);
  line-height: 1.8;
}

.preview-tips li {
  margin-bottom: 4px;
}

/* 错误提示 */
.error-toast {
  position: fixed;
  bottom: 24px;
  right: 24px;
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 14px 20px;
  background: #fef2f2;
  color: #dc2626;
  border: 1px solid #fecaca;
  border-radius: 10px;
  box-shadow: 0 10px 40px rgba(0,0,0,0.15);
  animation: slideIn 0.3s ease;
}

@keyframes slideIn {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.error-toast button {
  background: none;
  border: none;
  color: #dc2626;
  font-size: 18px;
  cursor: pointer;
  padding: 0 4px;
}
</style>
